import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router";

/**
 * **Focus follows an app-initiated route change** — design spec §15 rule 11,
 * LOCKED (added 2026-07-28, §14.2 item 6).
 *
 * Landing on `<body>` means the app replaced the entire screen and announced
 * nothing: a screen-reader user hears silence, a keyboard user restarts from
 * the document top. So on arrival, focus moves to the destination view's
 * primary heading.
 *
 * The rule, and each part of it:
 *
 * - **The heading is the announcement.** No extra live region, no `aria-live`
 *   wrapper — a focused `<h1>` is announced by every screen reader, and a
 *   second mechanism saying the same thing says it twice.
 * - **`tabIndex = -1`** so a heading can hold focus without joining the tab
 *   order. It is applied here rather than typed into every screen's JSX,
 *   because "every screen remembers" is not a mechanism.
 * - **No programmatic ring.** `--wr-focus` is bound to `:focus-visible`
 *   (`base.css` §15.2), so a mouse or touch user sees nothing. Nothing here
 *   sets `outline: none`; suppressing the ring for a keyboard user would break
 *   §15 rule 2.
 * - **Auto-focusing the email input instead is the tempting answer and it is
 *   wrong** (§14.2 item 6): on mobile it summons the keyboard the instant a
 *   modal disappears, and it covers the banner item 7 puts on that screen.
 *
 * ── What is NOT a route change for this purpose ─────────────────────────────
 *
 * 1. **The initial load.** Arriving is not a change; the document already has
 *    the user's attention.
 * 2. **Browser / hardware Back and Forward** (`POP`). The rule says
 *    *app-initiated*. Moving focus on a gesture the player made themselves is
 *    the app talking over them — and §14.2 item 9a bars trapping Back at all.
 * 3. **In-place changes that happen to write the URL.** `/login` ↔ `/signup`
 *    is one screen with two routes (§6 LOCKED); the toggle is a toggle, not a
 *    page load, and stealing focus off the control the player just operated is
 *    exactly the hostility this rule exists to prevent. Those call sites
 *    navigate with `IN_PLACE_ROUTE_STATE`, which is declared at the site that
 *    knows the truth rather than inferred here from a heuristic.
 * 4. **Overlay open/close.** Overlays never change the route; §11 and §12
 *    already trap and restore.
 */

/**
 * Marks a `navigate()` whose URL change is bookkeeping rather than an arrival.
 *
 * Spread into the navigate options: `navigate(to, { replace: true,
 * ...IN_PLACE_ROUTE_STATE })`. Merge it with any other state a call site
 * carries — `location.state` is a single object.
 */
export const IN_PLACE_ROUTE_STATE = { state: { inPlaceRouteChange: true } } as const;

/**
 * Whether an element is actually drawn.
 *
 * `display: none` elements have no client rects, and **you cannot focus one** —
 * `.focus()` on it is a silent no-op that leaves focus on `<body>`, which is
 * the exact failure this component exists to prevent.
 *
 * In jsdom nothing has layout and every element answers "no rects", so a
 * negative answer there must not disqualify anything — hence the caller's
 * "first rendered, else first" shape rather than a filter.
 */
function isRendered(node: HTMLElement): boolean {
  return node.getClientRects().length > 0;
}

/**
 * Where the announcement goes, in priority order:
 *
 * 1. `[data-route-heading]` — the screen's own declaration. Every routed screen
 *    marks its primary heading, including the `AuthCard` headline, which is an
 *    `<h1>` that does not live under a `<main>`.
 * 2. `main h1` — a safety net for a screen that forgets. Scoped to `main` so it
 *    can never pick the top bar's screen-title `<h1>`, which is chrome and
 *    belongs to the shell rather than to the destination.
 *
 * **A screen may declare more than one, and the visible one wins** (T-191 N5).
 * The lobby has two by design: Ivy §4.1 keeps the mobile greeting and the
 * desktop hero band in the DOM at every width and lets CSS choose between them,
 * because they are different content rather than a reflow of the same content.
 * Taking the first in document order therefore focused a `display: none`
 * greeting on every desktop arrival, and focus stayed on `<body>` — §15 rule
 * 11's target invisible on exactly the viewport that has a visible headline.
 *
 * No match at all is a no-op, not an error: the splash is a router and not a
 * destination (§5) and has no heading by design.
 */
export function findRouteHeading(root: ParentNode = document): HTMLElement | null {
  const declared = [...root.querySelectorAll<HTMLElement>("[data-route-heading]")];
  const heading = declared.find(isRendered) ?? declared[0];
  if (heading) return heading;

  const fallbacks = [...root.querySelectorAll<HTMLElement>("main h1")];
  return fallbacks.find(isRendered) ?? fallbacks[0] ?? null;
}

export function focusRouteHeading(root: ParentNode = document): HTMLElement | null {
  const heading = findRouteHeading(root);
  if (!heading) return null;
  heading.tabIndex = -1;
  heading.focus();
  return heading;
}

/**
 * Mounted once, inside the router and above `<Routes>`. Renders nothing.
 */
export function RouteFocus() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const mounted = useRef(false);

  const inPlace = (location.state as { inPlaceRouteChange?: boolean } | null)?.inPlaceRouteChange;

  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    if (navigationType === "POP") return;
    if (inPlace) return;
    focusRouteHeading();
    // `location.key` changes on every navigation, including one to the URL the
    // player is already on — which is a real arrival for a guard bounce.
  }, [location.key, navigationType, inPlace]);

  return null;
}
